import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSSE } from '@discovr/common';
import type { ReportExportEventName, ReportExportEventPayload } from '@discovr/types';
import { campaignKeys } from '../queryKeys';

export const REPORT_EXPORT_EVENTS: readonly ReportExportEventName[] = [
  'report_export.started',
  'report_export.ready',
  'report_export.failed',
  'report_export.cancelled',
];

interface Options {
  enabled: boolean;
  onEvent: (event: ReportExportEventName, payload: ReportExportEventPayload) => void;
}

function textList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Reads an SSE payload without trusting its shape; null when it cannot name a job and campaign. */
export function parseReportExportEvent(raw: unknown): ReportExportEventPayload | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const body = raw as Record<string, unknown>;
  const jobId = text(body.job_id);
  const campaignId = text(body.campaign_id);
  if (!jobId || !campaignId) return null;
  return {
    job_id: jobId,
    campaign_id: campaignId,
    file_name: text(body.file_name),
    status: text(body.status) as ReportExportEventPayload['status'],
    error_code: text(body.error_code) as ReportExportEventPayload['error_code'],
    warnings: textList(body.warnings),
  };
}

function useExportEventHandler(event: ReportExportEventName, onEvent: Options['onEvent']) {
  const queryClient = useQueryClient();
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  });
  return useCallback(
    (raw: Record<string, unknown>) => {
      const payload = parseReportExportEvent(raw);
      if (!payload) return;
      void queryClient.invalidateQueries({ queryKey: campaignKeys.reportExports(payload.campaign_id) });
      onEventRef.current(event, payload);
    },
    [event, queryClient],
  );
}

/** Refreshes the export list of the event's campaign, then lets the caller react (toasts).
 *  The requester and every watcher of the export receive these events, so the list is
 *  refreshed for whichever campaign the event names. */
export function useReportExportSSE({ enabled, onEvent }: Options): void {
  useSSE('report_export.started', enabled, useExportEventHandler('report_export.started', onEvent));
  useSSE('report_export.ready', enabled, useExportEventHandler('report_export.ready', onEvent));
  useSSE('report_export.failed', enabled, useExportEventHandler('report_export.failed', onEvent));
  useSSE('report_export.cancelled', enabled, useExportEventHandler('report_export.cancelled', onEvent));
}
